import {FlowEnum} from 'presentation/pages/private/booking/constants/flowEnum';
import {TelemedicineEnum} from 'presentation/pages/private/booking/constants/telemedicineEnum';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

import {NetworkTypesEnum} from 'domain/usecases';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {useHostName} from 'presentation/hooks';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {ChangeFlowVariantEnum} from 'presentation/pages/private/booking/components/ChangeFlowDialog/constants';
import {
	MAX_DISTANCE_FILTER_UNITS,
	MAX_DISTANCE_FILTER_UNITS_ODONTO,
	MIN_DISTANCE_IN_KM,
} from 'presentation/pages/private/booking/hooks/useBookingUnits/constants';
import {useQueryParamsSync} from 'presentation/pages/private/booking/hooks/useQueryParamsSync';
import {useSearchBar} from 'presentation/pages/private/booking/hooks/useSearchBar/useSearchBar';
import {UseUnitListProps} from './types';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';

import {Units} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';
import {getUnitsByDistanceTitle} from 'presentation/modules/booking/shared/utils/getUnitsByDistanceTitle';

const {
	changeDistance,
	changeFilter,
	searchNearbyUnitsHealthExam,
	changeAppointmentLocal,
	selfNetwork,
} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function useUnitList({
	list,
	flowType,
	networkType,
	showSpecialtyNotAvailableCard,
}: UseUnitListProps) {
	const {
		SEARCH_PARAMS: {localityDistance, query},
	} = useQueryParams();
	const {isGndiSaoPaulo} = useHostName();

	const {handleChangeModals} = useBookingSummary();
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);
	const {activeBookingFlowStep} = useQueryParamsSync();
	const {
		handleOpenDrawer,
		filterIsActive,
		districtGeolocationFilterIsActive,
		isDisabilityFilterActive,
	} = useSearchBar({
		activeStep: activeBookingFlowStep,
		flowType,
	});
	const bookingData = useBookingStore(state => state.bookingData);
	const {isManualLocation, hasGeoDistrictsFilter} = bookingData;

	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;

	const isDistanceFilterActive =
		Boolean(localityDistance) && localityDistance !== MIN_DISTANCE_IN_KM;

	const isSelfNetworkType = networkType === NetworkTypesEnum.SELF;

	const unitsListState = useUnitsListStore(state => state.unitsList);

	const hasAnyUnit = Boolean(
		unitsListState?.units?.unitsByLocality?.length ||
		unitsListState?.units?.unitsByDistance?.length ||
		unitsListState?.units?.unitsSupportAccredited?.length,
	);

	const listByLocality = list?.units?.unitsByLocality ?? [];
	const listByDistance = list?.units?.unitsByDistance ?? [];
	const listSupportAccredited = list?.units?.unitsSupportAccredited ?? [];

	const listByLocalityIsEmpty = listByLocality?.length === 0;
	const listByDistanceIsEmpty = listByDistance?.length === 0;
	const listSupportAccreditedEmpty = listSupportAccredited?.length === 0;

	const listByDistanceLength = listByDistance?.length;

	const hasOnlyTelemedicineUnit =
		listByLocality?.length === 1 &&
		(listByLocality[0] as Units<FlowEnum.HEALTH>)?.telemedicine ===
			TelemedicineEnum.IS_TELEMEDICINE;

	const supportAccreditNetworkList = isHealthFlow ? listSupportAccredited : listByLocality;
	const supportAccreditNetworkListIsEmpty = supportAccreditNetworkList?.length === 0;

	const unitsByDistanceTitle = getUnitsByDistanceTitle(listByDistanceLength, localityDistance);

	const showUnitsByDistanceTitle = isDistanceFilterActive;

	const distanceFilterAtMaxDistance = Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS;

	const showEmptySelftNetworkAlert =
		(listByLocalityIsEmpty && listByDistanceIsEmpty) || !isSelfNetworkType;

	const shouldChangeLocation =
		distanceFilterAtMaxDistance &&
		listByLocalityIsEmpty &&
		listByDistanceIsEmpty &&
		!supportAccreditNetworkListIsEmpty;

	const maxDistanceForFlow = isOdontoFlow
		? MAX_DISTANCE_FILTER_UNITS_ODONTO
		: MAX_DISTANCE_FILTER_UNITS;

	const isAtMaxDistanceWithoutUnits =
		isUnitsNetworksEnabled &&
		Number(localityDistance) >= maxDistanceForFlow &&
		showEmptySelftNetworkAlert;

	const shouldHideDivider = () => {
		if (isExamFlow || isOdontoFlow) return true;
		if (isGndiSaoPaulo) return supportAccreditNetworkListIsEmpty && query;
		return true;
	};

	const shouldHideFilterResultTitleInSupportAccreditedNetwork =
		listByLocality?.length > 0 || listByDistance?.length > 0;

	const shouldHideNetworkHeaderInSelfByLocality =
		(hasOnlyTelemedicineUnit && query && listByDistanceIsEmpty) ||
		showSpecialtyNotAvailableCard;
	const shouldHideNetworkHeaderInSelfByDistance = !listByLocalityIsEmpty;

	const shouldHideSelfNetworkButton =
		(isAtMaxDistanceWithoutUnits && !shouldChangeLocation) ||
		(distanceFilterAtMaxDistance &&
			hasOnlyTelemedicineUnit &&
			supportAccreditNetworkListIsEmpty &&
			query) ||
		(isOdontoFlow && isManualLocation) ||
		(bookingData?.isFavoriteDoctorsFlow &&
			(flowType === FlowEnum.HEALTH || flowType === FlowEnum.TELEHEALTH));

	const shouldShowDistanceReferenceInOdontoFlow =
		isOdontoFlow && !isManualLocation && !hasGeoDistrictsFilter;

	const showDistanceReferenceInSelfNetworkLocalityList = shouldShowDistanceReferenceInOdontoFlow;
	const showDistanceReferenceInSupportAccreditedNetwork = shouldShowDistanceReferenceInOdontoFlow;

	const shouldHideSelfNetworkComponent = () => {
		if (isOdontoFlow && !isSelfNetworkType) {
			return true;
		}

		if (!query || !hasAnyUnit) {
			return false;
		}

		if (!isSelfNetworkType || (listByLocalityIsEmpty && listByDistanceIsEmpty)) {
			return true;
		}

		return false;
	};
	const openDrawerButtonLabel = () => {
		if (filterIsActive) {
			return districtGeolocationFilterIsActive || isDisabilityFilterActive
				? changeFilter
				: changeDistance;
		}

		return searchNearbyUnitsHealthExam;
	};
	const handleChangeLocation = () => handleChangeModals(ChangeFlowVariantEnum.LOCATION);
	const selfNetworkButtonLabel = () => {
		if (shouldChangeLocation) {
			return changeAppointmentLocal;
		}

		return openDrawerButtonLabel();
	};
	const selfNetworkButtonAction = () => {
		if (shouldChangeLocation) {
			return handleChangeLocation();
		}

		return handleOpenDrawer();
	};
	const selfNetworkButtonDataTagId = () => {
		if (shouldChangeLocation) {
			return AppointmentBookingEnum.CLICK_ALTERAR_LOCAL_ATENDIMENTO_AGENDAMENTO;
		}

		return filterIsActive
			? AppointmentBookingEnum.ALTEROU_DISTANCIA
			: AppointmentBookingEnum.PROCUROU_UNIDADES_PROXIMAS;
	};
	const getEmptySelfNetworkAlertDescription = () => {
		if (isDistanceFilterActive && !isAtMaxDistanceWithoutUnits) {
			return `${selfNetwork.emptyDescriptionInKmPart1}${localityDistance}${selfNetwork.emptyDescriptionInKmPart2}`;
		}

		return selfNetwork.emptyDescription;
	};
	const getNetworkHeaderDescription = () => {
		if (hasOnlyTelemedicineUnit && listByDistanceIsEmpty) {
			return getEmptySelfNetworkAlertDescription();
		}
		if (isExamFlow) {
			return selfNetwork.onlineScheduleExamDescription;
		}

		return selfNetwork.onlineScheduleDescription;
	};

	return {
		listByLocality,
		listByDistance,
		listSupportAccredited,
		listByLocalityIsEmpty,
		listByDistanceIsEmpty,
		listSupportAccreditedEmpty,
		listByDistanceLength,
		hasOnlyTelemedicineUnit,
		supportAccreditNetworkList,
		supportAccreditNetworkListIsEmpty,
		unitsByDistanceTitle,
		handleOpenDrawer,
		filterIsActive,
		openDrawerButtonLabel,
		selfNetworkButtonLabel,
		selfNetworkButtonAction,
		selfNetworkButtonDataTagId,
		getEmptySelfNetworkAlertDescription,
		showEmptySelftNetworkAlert,
		showUnitsByDistanceTitle,
		getNetworkHeaderDescription,
		shouldHideDivider,
		shouldHideFilterResultTitleInSupportAccreditedNetwork,
		shouldHideNetworkHeaderInSelfByLocality,
		shouldHideSelfNetworkComponent,
		shouldHideSelfNetworkButton,
		shouldHideNetworkHeaderInSelfByDistance,
		showDistanceReferenceInSelfNetworkLocalityList,
		showDistanceReferenceInSupportAccreditedNetwork,
	};
}
